import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const renderInline = (text, keyPrefix) => (
  String(text).split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
  })
);

const renderNote = (content, index) => {
  const paragraphs = content.split(/\n\n+/).map((part) => part.trim()).filter(Boolean);
  const [first = '', ...rest] = paragraphs;
  const [label, ...labelRest] = first.split('\n');
  const bodies = [labelRest.join('\n').trim(), ...rest].filter(Boolean);

  return (
    <div
      key={index}
      className="my-4 rounded-r-md border border-amber-200 border-l-4 border-l-orange-500 bg-amber-50 px-4 py-3 text-foreground"
    >
      <p className="font-semibold text-orange-600">{label}</p>
      {bodies.map((paragraph, paragraphIndex) => (
        <p key={paragraphIndex} className="mt-2">
          {renderInline(paragraph, `note-${index}-${paragraphIndex}`)}
        </p>
      ))}
    </div>
  );
};

const renderBlock = (block, index) => {
  const lines = block.split('\n').filter((line) => line.length > 0);
  if (lines.length === 0) return null;

  if (lines.every((line) => line.startsWith('• '))) {
    return (
      <ul key={index} className="my-3 list-disc space-y-1 pl-5">
        {lines.map((line) => (
          <li key={line}>{renderInline(line.slice(2), line)}</li>
        ))}
      </ul>
    );
  }

  if (lines.every((line) => /^\d+\.\s/.test(line))) {
    return (
      <ol key={index} className="my-3 list-decimal space-y-1 pl-5">
        {lines.map((line) => (
          <li key={line}>{renderInline(line.replace(/^\d+\.\s/, ''), line)}</li>
        ))}
      </ol>
    );
  }

  const [first, ...rest] = lines;
  const isSectionLabel = lines.length === 1 && first.length <= 80 && !/[.:]$/.test(first);
  const isTitledStep = rest.length > 0 && /^\d+\.\s/.test(first) && !first.endsWith('.');
  const isHeading = isSectionLabel || isTitledStep;

  return (
    <div key={index} className="my-3 first:mt-0">
      {isHeading ? (
        <p className="font-medium text-foreground">{renderInline(first, `h-${index}`)}</p>
      ) : null}
      {isSectionLabel ? null : (
        <p className={isHeading ? 'mt-1' : undefined}>
          {(isHeading ? rest : lines).map((line, lineIndex) => (
            <React.Fragment key={`${line}-${lineIndex}`}>
              {lineIndex > 0 ? <br /> : null}
              {renderInline(line, `p-${index}-${lineIndex}`)}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

const formatAnswer = (text) => {
  const source = String(text || '');
  const nodes = [];
  const notePattern = /\[\[note\]\]([\s\S]*?)\[\[\/note\]\]/g;
  let lastIndex = 0;
  let blockIndex = 0;
  let match = notePattern.exec(source);

  const pushText = (chunk) => {
    chunk.split(/\n\n+/).map((part) => part.trim()).filter(Boolean).forEach((block) => {
      nodes.push(renderBlock(block, blockIndex));
      blockIndex += 1;
    });
  };

  while (match) {
    pushText(source.slice(lastIndex, match.index));
    nodes.push(renderNote(match[1].trim(), blockIndex));
    blockIndex += 1;
    lastIndex = match.index + match[0].length;
    match = notePattern.exec(source);
  }

  pushText(source.slice(lastIndex));
  return nodes;
};

const FAQItem = ({ question, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`${!isLast ? 'border-b border-border' : ''}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start justify-between p-4 md:p-6 hover:bg-accent/30 transition-colors text-left"
      >
        <div className="flex-1 pr-4">
          <h3 className="text-sm md:text-base font-medium text-foreground">
            {question?.question}
          </h3>
        </div>
        <Icon
          name={isExpanded ? "Minus" : "Plus"}
          size={18}
          className="text-muted-foreground flex-shrink-0 mt-0.5"
        />
      </button>
      {isExpanded && (
        <div className="px-4 md:px-6 pb-4 md:pb-6">
          <div className="pl-0 md:pl-2">
            <div className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {formatAnswer(question?.answer)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQItem;
